'use client';

import { ContentDialog } from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';

import './skimming-guide-animation.css';

const TRACK_CONTAINER =
  'absolute left-[34px] right-[34px] top-[39px] flex flex-col gap-[2.8px]';
const TRACK_ROW = 'flex gap-[2.8px] items-center h-[28px]';

function SkimmerCap() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
    >
      <rect
        width="12"
        height="12"
        rx="3"
        fill="var(--comp-label-yellow-bg)"
      />
    </svg>
  );
}

function SkimmerBar({ className }: { className?: string }) {
  return (
    <div className={className}>
      <SkimmerCap />
      <div className="mx-auto h-[93px] w-4xs bg-comp-label-yellow-bg" />
    </div>
  );
}

function CursorIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M4 1L4 15L8.5 10.5L13 18L15.5 16.5L11 9L17 9L4 1Z"
        fill="var(--sys-cont-none-light)"
        stroke="var(--sys-cont-neutral-default)"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SkimmingPreviewAnimation() {
  return (
    <div className="relative h-[140px] w-full overflow-hidden rounded-sm bg-sys-bg-surface-2">
      <div className={TRACK_CONTAINER}>
        <div className={TRACK_ROW}>
          <div className="h-full w-[84px] shrink-0 rounded-[5.6px] bg-sys-style-primary-default" />
          <div className="h-full w-[141px] shrink-0 rounded-[5.6px] bg-sys-style-primary-default" />
          <div className="h-full min-w-px flex-1 rounded-[5.6px] bg-sys-style-primary-default" />
        </div>
        <div className={TRACK_ROW}>
          <div className="h-full w-[84px] shrink-0 rounded-[5.6px] bg-comp-label-green-bg" />
          <div className="h-full w-[141px] shrink-0 rounded-[5.6px] bg-comp-label-green-bg" />
          <div className="h-full min-w-px flex-1 rounded-[5.6px] bg-comp-label-green-bg" />
        </div>
      </div>

      <div className="animate-skimmer-slide absolute top-[15px]">
        <SkimmerBar className="flex flex-col items-center" />
        <div className="absolute top-[30px] left-[11px]">
          <CursorIcon />
        </div>
      </div>
    </div>
  );
}

function SkimmingEditAnimation() {
  return (
    <div className="relative h-[140px] w-full overflow-hidden rounded-sm bg-sys-bg-surface-2">
      <div className={TRACK_CONTAINER}>
        <div className="animate-skimmer-edit-split flex h-[28px] items-center">
          <div className="relative h-full w-[50%] overflow-hidden rounded-l-[5.6px] bg-sys-style-primary-default">
            <div className="animate-skimmer-edit-trim-left absolute inset-0 bg-ref-alpha-light-70" />
          </div>
          <div className="relative h-full w-[50%] overflow-hidden rounded-r-[5.6px] bg-sys-style-primary-default">
            <div className="animate-skimmer-edit-trim-right absolute inset-0 bg-ref-alpha-light-70" />
          </div>
        </div>
        <div className="animate-skimmer-edit-split flex h-[28px] items-center">
          <div className="relative h-full w-[50%] overflow-hidden rounded-l-[5.6px] bg-comp-label-green-bg">
            <div className="animate-skimmer-edit-trim-left absolute inset-0 bg-ref-alpha-light-70" />
          </div>
          <div className="relative h-full w-[50%] overflow-hidden rounded-r-[5.6px] bg-comp-label-green-bg">
            <div className="animate-skimmer-edit-trim-right absolute inset-0 bg-ref-alpha-light-70" />
          </div>
        </div>
      </div>

      <SkimmerBar className="animate-skimmer-edit-bar absolute top-[15px] flex -translate-x-1/2 flex-col items-center" />

      <div
        className="pointer-events-none absolute top-[53px]"
        style={{ left: 'calc(50% + 15px)' }}
      >
        <div className="animate-skimmer-edit-key-q absolute flex size-[28px] items-center justify-center rounded-3xs bg-ref-alpha-light-70 text-[16px] font-bold text-black">
          Q
        </div>
        <div className="animate-skimmer-edit-key-w absolute flex size-[28px] items-center justify-center rounded-3xs bg-ref-alpha-light-70 text-[16px] font-bold text-black">
          W
        </div>
        <div className="animate-skimmer-edit-key-e absolute flex size-[28px] items-center justify-center rounded-3xs bg-ref-alpha-light-70 text-[16px] font-bold text-black">
          E
        </div>
      </div>
    </div>
  );
}

interface SkimmingGuideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SkimmingGuideDialog({
  open,
  onOpenChange,
}: SkimmingGuideDialogProps) {
  const t = useTranslations();

  return (
    <ContentDialog
      open={open}
      setOpen={onOpenChange}
      titleNode={t('Editor.timeline-controls.skimming-guide-title')}
      descriptionNode={t('Editor.timeline-controls.skimming-guide-description')}
      isShowCancelButton={false}
      confirmText={t('Editor.timeline-controls.skimming-guide-confirm')}
      onConfirm={() => onOpenChange(false)}
      contentClassName="w-[600px] max-w-[calc(100vw-48px)]"
    >
      <div className="flex flex-col gap-2xs">
        <SkimmingPreviewAnimation />

        <div className="flex flex-col gap-4xs">
          <p className="text-body-medium text-sys-cont-neutral-default">
            {t('Editor.timeline-controls.skimming-guide-edit-description')}
          </p>
          <p className="text-body-small text-sys-cont-neutral-lightest">
            {t('Editor.timeline-controls.skimming-guide-edit-sub')}
          </p>
        </div>

        <SkimmingEditAnimation />
      </div>
    </ContentDialog>
  );
}
